import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { isUrlValid } from "../../../../utils/utils";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import catoAccountInfoButton from "./images/Cato-account-info-btn.png";
import catoAPIKeysButton from "./images/Cato-api-keys-btn.png";
import catoCopyAccountId from "./images/Cato-copy-account-id.png";
import catoCreateAPIKey from "./images/Cato-create-api-key.png";
import catoNewButton from "./images/Cato-new-btn.png";

const CATO_CODE_NAME = "cato";

export default function CatoIntegration() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [accountId, setAccountId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [consoleUrl, setConsoleUrl] = useState("");
  const [showConsoleUrlError, setShowConsoleUrlError] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const { registerCatoIntegration } = useAPI();
  const [registerIsLoading, setRegisterIsLoading] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(CATO_CODE_NAME);
  const { staticIPs: allowlistIps } = useStaticIPs();
  const guideMode = searchParams.has("viewonly");

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const onSubmit = async () => {
    const normalizedUrl = normalizeConsoleUrl(consoleUrl);

    if (
      !isUrlValid(normalizedUrl) ||
      !normalizedUrl.includes("catonetworks.com")
    ) {
      setShowConsoleUrlError(true);
      return;
    }
    setRegisterIsLoading(true);
    try {
      await registerCatoIntegration(accountId, apiKey, normalizedUrl);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Cato registered successfully");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setRegisterIsLoading(false);
    }
  };

  const normalizeConsoleUrl = (url) => {
    if (!url) return url;
    let normalizedUrl = url.trim();

    if (!/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    try {
      const parsedUrl = new URL(normalizedUrl);
      return `${parsedUrl.protocol}//${parsedUrl.hostname}`;
    } catch (e) {
      return normalizedUrl;
    }
  };

  const handleConsoleUrlChange = (e) => {
    setConsoleUrl(e.target.value);
    setShowConsoleUrlError(false);
  };

  return (
    <Box>
      <Guideline
        title={isIntegrated ? "" : "Cato integration instructions"}
        image={logos.CatoLogo}
        iconBorderRadius={0}
        name={"Cato"}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Cato is registered!</Typography>
            </Box>
          ) : (
            <Box>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Cato Management Application">
                  <TextWithExternalLink
                    text="Open your"
                    linkText="Cato Management Application"
                    href="https://auth.catonetworks.com/"
                  />
                </GuidelineStep>
                <GuidelineStep title="Enter your console URL">
                  <Typography variant="body2">
                    Copy the Console URL from your browser address bar (e.g.,
                    https://cato.cc.catonetworks.com/) and paste it below
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Find your Account ID">
                  <Typography variant="body2">
                    Click <b>Account -{">"} Account info</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: catoAccountInfoButton,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">Copy your Account id</Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: catoCopyAccountId,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Paste the account ID below
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Generate API Key">
                  <Typography variant="body2">
                    Click <b>Resources -{">"} API Keys</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: catoAPIKeysButton,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Click <b>New</b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: catoNewButton,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">Enter a Key Name</Typography>
                  <Typography variant="body2">
                    Select <b>View</b> API Permission
                  </Typography>
                  <Typography variant="body2">
                    Allow access from <b>Specific IP list</b> and enter the next
                    addresses:
                  </Typography>
                  {allowlistIps.map((ip, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                        marginLeft: "20px",
                      }}
                    >
                      <Typography variant="body1">{ip}</Typography>
                      <CustomCopyButton
                        copyTitle={"IP Address"}
                        copyText={ip}
                      />
                    </Box>
                  ))}
                  <Typography variant="body2">
                    Click the Apply button
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: catoCreateAPIKey,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant="body2">
                    Copy the API Key and paste it below
                  </Typography>
                  <Typography variant="body2">
                    Click OK to close the pop-up window
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Enter your credentials">
                  <Box
                    width={700}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    <TextField
                      placeholder="Console URL"
                      fullWidth
                      type="text"
                      value={consoleUrl}
                      onChange={handleConsoleUrlChange}
                      disabled={guideMode}
                      error={showConsoleUrlError}
                      helperText={
                        showConsoleUrlError &&
                        "Please enter a valid Cato console URL (e.g., https://cato.cc.catonetworks.com/)"
                      }
                    />
                    <TextField
                      placeholder="Account ID"
                      fullWidth
                      type="text"
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      disabled={guideMode}
                    />
                    <TextField
                      placeholder="API Key"
                      fullWidth
                      type={showApiKey ? "text" : "password"}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      disabled={guideMode}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle api key visibility"
                              onClick={() => setShowApiKey((prev) => !prev)}
                            >
                              {showApiKey ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <ContainedButton
                      onClick={onSubmit}
                      size="small"
                      color="primary"
                      disabled={
                        guideMode || !accountId || !apiKey || !consoleUrl
                      }
                      loading={registerIsLoading}
                    >
                      Connect
                    </ContainedButton>
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          )
        }
      />
    </Box>
  );
}
